import { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { SponsorBanner, AdSensePlaceholder, ClickBankBanner } from './AdSlots';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SizeComparison() {
  const [brands, setBrands] = useState([]);
  const [fromBrand, setFromBrand] = useState('');
  const [toBrand, setToBrand] = useState('');
  const [category, setCategory] = useState('mens_tops');
  const [sizeLabel, setSizeLabel] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromBrandData, setFromBrandData] = useState(null);

  useEffect(() => {
    setCanonical('/compare');
    setMeta('Compare Sizes Between Brands', 'Compare clothing sizes between Nike, Zara, H&M, and 50+ other brands.');
    axios.get(`${API}/brands?limit=500`).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (fromBrand) {
      const b = brands.find(b => b.slug === fromBrand);
      setFromBrandData(b || null);
      setSizeLabel('');
      setResult(null);
    }
  }, [fromBrand, brands]);

  const availableCats = fromBrandData?.categories?.map(c => c.slug) || [];
  const currentCat = fromBrandData?.categories?.find(c => c.slug === category);
  const availableSizes = currentCat?.sizes || [];

  const handleCompare = async () => {
    if (!fromBrand || !toBrand || !sizeLabel) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/size/convert`, { from_brand: fromBrand, to_brand: toBrand, category, size_label: sizeLabel });
      setResult(r.data);
    } catch (e) { setResult({ error: 'Could not compare. Make sure both brands have this category.' }); }
    setLoading(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tighter text-[#1C1917] mb-4">Compare Sizes</h1>
        <p className="text-[#78716C]">I'm a size __ in Brand A. What size am I in Brand B?</p>
      </div>

      <div className="bg-white border border-[#E8E2D9] p-6 sm:p-8 space-y-6">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-[#1C1917] mb-2">Category</label>
          <div className="flex flex-wrap gap-2">
            {[{ v: 'mens_tops', l: "Men's Tops" }, { v: 'womens_tops', l: "Women's Tops" }, { v: 'mens_shoes', l: "Men's Shoes" }, { v: 'womens_shoes', l: "Women's Shoes" }, { v: 'mens_pants', l: "Men's Pants" }].map(c => (
              <button key={c.v} onClick={() => { setCategory(c.v); setSizeLabel(''); setResult(null); }} className={`px-4 py-2 text-xs font-medium border transition-colors ${category === c.v ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'bg-white text-[#78716C] border-[#E8E2D9] hover:border-[#C5A028]'}`} data-testid={`compare-cat-${c.v}`}>{c.l}</button>
            ))}
          </div>
        </div>

        {/* Brands */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-[#1C1917] mb-2">I wear in...</label>
            <select value={fromBrand} onChange={e => setFromBrand(e.target.value)} className="w-full p-3 border border-[#E8E2D9] bg-[#FAFAFA] focus:border-[#C5A028] focus:outline-none text-sm" data-testid="compare-from-brand">
              <option value="">Select brand</option>
              {brands.filter(b => b.categories?.some(c => c.slug === category)).map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
          </div>
          <div className="flex justify-center">
            <ArrowRight className="w-5 h-5 text-[#C5A028]" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-[#1C1917] mb-2">What's my size in...</label>
            <select value={toBrand} onChange={e => { setToBrand(e.target.value); setResult(null); }} className="w-full p-3 border border-[#E8E2D9] bg-[#FAFAFA] focus:border-[#C5A028] focus:outline-none text-sm" data-testid="compare-to-brand">
              <option value="">Select brand</option>
              {brands.filter(b => b.slug !== fromBrand && b.categories?.some(c => c.slug === category)).map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
          </div>
        </div>

        {/* Size Selection */}
        {fromBrand && availableSizes.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-2">My size</label>
            <div className="flex flex-wrap gap-2">
              {availableSizes.map(s => {
                const lbl = s.label || s.us;
                return (
                  <button key={lbl} onClick={() => { setSizeLabel(lbl); setResult(null); }} className={`px-4 py-2 text-sm font-medium border transition-colors ${sizeLabel === lbl ? 'bg-[#C5A028] text-white border-[#C5A028]' : 'bg-white text-[#78716C] border-[#E8E2D9] hover:border-[#C5A028]'}`} data-testid={`size-${lbl}`}>{lbl}</button>
                );
              })}
            </div>
          </div>
        )}

        <button onClick={handleCompare} disabled={!fromBrand || !toBrand || !sizeLabel || loading} className="w-full py-3 bg-[#1C1917] text-white font-medium text-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50" data-testid="compare-submit">
          {loading ? 'Comparing...' : 'Compare Sizes'}
        </button>
      </div>

      {/* Result */}
      {result && !result.error && (
        <div className="mt-8 bg-white border-2 border-[#C5A028] p-6 sm:p-8">
          <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C1917] mb-4 text-center">Your Size Comparison</h3>
          <div className="grid grid-cols-5 gap-4 items-center text-center">
            <div className="col-span-2">
              <p className="font-semibold text-[#1C1917]">{result.from_brand}</p>
              <p className="text-2xl font-bold text-[#C5A028] mt-1">{result.from_size?.label || result.from_size?.us}</p>
              {result.from_fit_note && <p className="text-xs text-[#78716C] mt-2 italic">{result.from_fit_note}</p>}
            </div>
            <div><ArrowRight className="w-6 h-6 text-[#C5A028] mx-auto" /></div>
            <div className="col-span-2">
              <p className="font-semibold text-[#1C1917]">{result.to_brand}</p>
              <p className="text-2xl font-bold text-[#C5A028] mt-1">{result.to_size?.label || result.to_size?.us}</p>
              {result.to_fit_note && <p className="text-xs text-[#78716C] mt-2 italic">{result.to_fit_note}</p>}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            {result.to_size && Object.entries(result.to_size).filter(([k]) => ['us', 'uk', 'eu', 'jp', 'kr'].includes(k)).map(([k, v]) => (
              <div key={k} className="flex justify-between bg-[#FAF8F4] p-3">
                <span className="text-[#78716C] uppercase text-xs font-semibold">{k}</span>
                <span className="font-medium text-[#1C1917]">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {result?.error && (
        <div className="mt-8 bg-red-50 border border-red-200 p-4 text-sm text-red-600">{result.error}</div>
      )}

      <div className="mt-8 text-xs text-[#78716C] text-center">Sizes are approximate. Fit may vary by style and cut.</div>
      <SponsorBanner />
      <ClickBankBanner />
      <AdSensePlaceholder />
    </div>
  );
}
